/**
 * Storefront ad tags. Scripts are inserted as real DOM nodes.
 * react-helmet script tags do not execute, which is why saved pixels never fired.
 */

const ID = {
  ga4: /^G-[A-Z0-9]{4,20}$/i,
  gtm: /^GTM-[A-Z0-9]{4,16}$/i,
  aw: /^AW-\d{6,15}$/i,
  pixel: /^\d{8,20}$/,
  clarity: /^[a-z0-9]{6,32}$/i,
  label: /^[A-Za-z0-9_-]{3,40}$/,
};

function clean(value, kind) {
  const s = String(value || "").trim();
  if (!s || !ID[kind] || !ID[kind].test(s)) return "";
  return kind === "label" || kind === "clarity" || kind === "pixel" ? s : s.toUpperCase();
}

function readAds(ads) {
  const a = ads || {};
  return {
    ga4: clean(a.ga4_id, "ga4"),
    gtm: clean(a.gtm_id, "gtm"),
    aw: clean(a.google_ads_id, "aw"),
    label: clean(a.google_ads_label, "label"),
    ytLabel: clean(a.youtube_ads_label, "label"),
    pixel: clean(a.fb_pixel_id, "pixel"),
    clarity: clean(a.clarity_id, "clarity"),
  };
}

function ensureExternal(id, src) {
  let el = document.getElementById(id);
  if (el && el.getAttribute("src") === src) return;
  if (el) el.remove();
  el = document.createElement("script");
  el.id = id;
  el.async = true;
  el.src = src;
  document.head.appendChild(el);
}

function ensureInline(id, code) {
  let el = document.getElementById(id);
  if (el && el.textContent === code) return;
  if (el) el.remove();
  el = document.createElement("script");
  el.id = id;
  el.text = code;
  document.head.appendChild(el);
}

export function installStoreAds(ads) {
  if (typeof document === "undefined") return readAds(ads);
  const tags = readAds(ads);
  window.__msAds = tags;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };

  if (tags.gtm && window.__msGtm !== tags.gtm) {
    window.__msGtm = tags.gtm;
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    ensureExternal("ms-gtm", `https://www.googletagmanager.com/gtm.js?id=${tags.gtm}`);
  }

  const gtagIds = [tags.ga4, tags.aw].filter(Boolean);
  if (gtagIds.length) {
    ensureExternal("ms-gtag", `https://www.googletagmanager.com/gtag/js?id=${gtagIds[0]}`);
    const configs = gtagIds.map((id) => `gtag('config','${id}',{send_page_view:false});`).join("");
    ensureInline("ms-gtag-config", `window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){dataLayer.push(arguments);};gtag('js',new Date());${configs}`);
  }

  if (tags.clarity && window.__msClarity !== tags.clarity) {
    window.__msClarity = tags.clarity;
    ensureInline(
      "ms-clarity",
      `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${tags.clarity}");`,
    );
  }

  if (tags.pixel && window.__msPixel !== tags.pixel) {
    window.__msPixel = tags.pixel;
    ensureInline(
      "ms-fbpixel",
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${tags.pixel}');`,
    );
  }
  return tags;
}

const FB = {
  page_view: "PageView",
  view_item: "ViewContent",
  add_to_cart: "AddToCart",
  begin_checkout: "InitiateCheckout",
  purchase: "Purchase",
};

export function trackStoreEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  const tags = window.__msAds || {};
  const payload = {
    value: params.value != null ? Number(params.value) : undefined,
    currency: params.currency || "INR",
    content_ids: params.content_ids || undefined,
    content_name: params.content_name || undefined,
    content_type: params.content_ids ? "product" : undefined,
    transaction_id: params.transaction_id || undefined,
  };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...payload, page_path: window.location.pathname });
  if (typeof window.gtag === "function" && (tags.ga4 || tags.aw)) {
    window.gtag("event", name === "page_view" ? "page_view" : name, {
      ...payload,
      page_path: window.location.pathname,
      send_to: tags.ga4 || undefined,
    });
    if (name === "purchase" && tags.aw) {
      [tags.label, tags.ytLabel].filter(Boolean).forEach((label) => {
        window.gtag("event", "conversion", {
          send_to: `${tags.aw}/${label}`,
          value: payload.value,
          currency: payload.currency,
          transaction_id: payload.transaction_id,
        });
      });
    }
  }
  if (tags.pixel && typeof window.fbq === "function" && FB[name]) {
    const fbPayload = {};
    if (payload.value != null && name !== "page_view") fbPayload.value = payload.value;
    if (name !== "page_view") fbPayload.currency = payload.currency;
    if (payload.content_ids) fbPayload.content_ids = payload.content_ids;
    if (payload.content_name) fbPayload.content_name = payload.content_name;
    if (payload.content_type) fbPayload.content_type = payload.content_type;
    window.fbq("track", FB[name], fbPayload);
  }
}
